/* eslint-disable no-dupe-class-members */
import type { PollStorage, PollStoredState } from 'components-machine';
import { addMonths } from 'date-fns';
import { isEqual } from 'lodash';
import lz from 'lz-string';
import { fetchWithTimeout } from 'src/utils';

const isClientError = (response: Response) => {
  return Math.floor(response.status / 100) === 4;
};

type PollSiteApiResponse = {
  content: string;
  poll_id: string;
  received_at: string;
  trackingId: string;
};

/** Объект для работы с хранилищем Polls site */
export class PollsSiteStorage implements PollStorage {
  private pollId: string;
  private trackingId: string | null;
  private expireTimeMs: number | null;

  private apiUrl: string;

  private hasCache: boolean;
  private cache: Partial<PollStoredState> | null;

  private processingGetRequest: Promise<Partial<PollStoredState> | null> | null;
  private processingPostRequest: Promise<void> | null;
  private postQueue: Partial<PollStoredState>[];

  private readonly fetchTimeout = 5_000;

  /**
   * Создание хранилища
   * @param {object} config - конфигурация
   * @param {string} config.pollId - id опроса
   * @param {string} config.trackingId - уникальный идентификатор контекста прохождения Polls site. Может быть использована любая строка - нет заданного формата
   * @param {number} [config.expireTimeMs] - время жизни контекста прохождения Polls site в миллисекундах
   */
  constructor({
    pollId,
    trackingId,
    expireTimeMs,
  }: {
    pollId: string;
    trackingId: string | null;
    expireTimeMs: number | null;
  }) {
    this.pollId = pollId;
    this.trackingId = trackingId;
    this.expireTimeMs = expireTimeMs;

    this.hasCache = false;
    this.cache = null;

    this.processingGetRequest = null;
    this.processingPostRequest = null;
    this.postQueue = [];

    try {
      const url = new URL(window.APP_IC_CONTEXT_URL);
      url.searchParams.append('poll_id', this.pollId);

      // MACP-1647 Реализовать отправку referer вместо tracking_id в context со стороны компонентов
      if (this.trackingId) url.searchParams.append('tracking_id', this.trackingId);

      this.apiUrl = url.toString();
    } catch {
      this.apiUrl = '';
    }
  }

  /**
   * Отправляет запрос получения контекста прохождения Polls site, либо получает данные из кэша
   */
  private async getStoredState() {
    try {
      const fetchStoredState = async () => {
        const response = await fetchWithTimeout(this.apiUrl, {
          credentials: 'same-origin',
          timeout: this.fetchTimeout,
        });

        if (isClientError(response)) {
          return null;
        }

        const { content: dataString } = (await response.json()) as PollSiteApiResponse;

        if (!dataString) {
          return null;
        }

        try {
          const data = this.parse(dataString);
          return data;
        } catch (error) {
          console.error(error);
        }

        return null;
      };

      if (this.hasCache) {
        return this.cache;
      }

      if (!this.processingGetRequest) {
        this.processingGetRequest = fetchStoredState();
      }

      const state = await this.processingGetRequest;

      this.hasCache = true;
      this.cache = state;
      this.processingGetRequest = null;

      return state;
    } catch (error) {
      this.hasCache = false;
      this.cache = null;
      this.processingGetRequest = null;

      throw error;
    }
  }

  /**
   * Получение состояния прохождения из хранилища
   * @param {string} [key] - свойство состояния, значение которого будет возвращено
   */
  public get(): Promise<Partial<PollStoredState> | null>;
  public get<TKey extends keyof PollStoredState>(key: TKey): Promise<PollStoredState[TKey] | null>;
  public async get<TKey extends keyof PollStoredState>(key?: TKey) {
    const state = await this.getStoredState();

    return key ? state?.[key] ?? null : state;
  }

  /**
   * Отправляет запрос создания контекста прохождения Polls site
   * @param {object} data - состояние прохождение опроса
   */
  private createContext(data: Partial<PollStoredState>) {
    const { pollId, trackingId, expireTimeMs } = this;
    const stringifiedData = this.stringify(data);

    const expiredAt = expireTimeMs ? new Date(expireTimeMs) : addMonths(Date.now(), 3);

    return fetchWithTimeout(this.apiUrl, {
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      keepalive: true,
      body: JSON.stringify({
        content: stringifiedData,
        poll_id: pollId,
        expired_at: expiredAt,
        // MACP-1647 Реализовать отправку referer вместо tracking_id в context со стороны компонентов
        ...(trackingId ? { tracking_id: trackingId } : { referer: window.location.href }),
      }),
      timeout: this.fetchTimeout,
    });
  }

  /**
   * Отправляет запрос обновления контекста прохождения Polls site
   * @param {object} data - состояние прохождение опроса
   */
  private updateContext(data: Partial<PollStoredState>) {
    const stringifiedData = this.stringify(data);

    return fetchWithTimeout(this.apiUrl, {
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      keepalive: true,
      body: JSON.stringify({ content: stringifiedData }),
      timeout: this.fetchTimeout,
    });
  }

  /**
   * Обновляет состояние прохождения в хранилище
   * @param {object} data - состояние прохождение опроса, возможно использование неполного состояния - остальные будут взяты из предыдущего состояния
   */
  public async update(data: Partial<PollStoredState>): Promise<void> {
    const fetchUpdate = async (data: Partial<PollStoredState>) => {
      try {
        const previousData = await this.get();
        const dataToSet = { ...previousData, ...data };

        if (isEqual(previousData, dataToSet)) {
          return;
        }

        const { fetchMethod, fallbackMethod } = previousData
          ? { fetchMethod: this.updateContext.bind(this), fallbackMethod: this.createContext.bind(this) }
          : { fetchMethod: this.createContext.bind(this), fallbackMethod: this.updateContext.bind(this) };

        let responsePromise = fetchMethod(dataToSet);
        let response = await responsePromise;

        if (response.status === 409) {
          responsePromise = fallbackMethod(dataToSet);
        }

        response = await responsePromise;

        if (isClientError(response)) {
          return;
        }

        const { content: updatedData } = (await response.json()) as PollSiteApiResponse;

        this.cache = this.parse(updatedData);
        this.hasCache = true;
      } finally {
        const data = this.postQueue.shift();

        if (data) {
          this.processingPostRequest = fetchUpdate(data);
          await this.processingPostRequest;
        } else {
          this.processingPostRequest = null;
        }
      }
    };

    if (this.processingPostRequest) {
      this.postQueue.push(data);
    } else {
      this.processingPostRequest = fetchUpdate(data);
    }

    await this.processingPostRequest;
  }

  /**
   * Удалить состояние прохождения опроса
   */
  public async remove(): Promise<void> {
    try {
      await fetchWithTimeout(this.apiUrl, { method: 'delete', credentials: 'same-origin', timeout: this.fetchTimeout });
    } catch (error) {
      console.error(error);
    } finally {
      this.cache = null;
      this.hasCache = false;
    }
  }

  /**
   * Cериализовать данные состояния прохождения
   * @param {object} data - состояние прохождения опроса
   */
  private stringify(data: Partial<PollStoredState>): string {
    return lz.compressToEncodedURIComponent(JSON.stringify(data));
  }

  /**
   * Десериализовать данные состояния прохождения
   * @param {string} dataString - сериализованные данные
   */
  private parse(dataString: string): Partial<PollStoredState> | null {
    const jsonData = lz.decompressFromEncodedURIComponent(dataString);

    if (!jsonData) {
      console.error('No data');
      return null;
    }

    const data = JSON.parse(jsonData) as Partial<PollStoredState> | null;

    return data;
  }
}
